import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { ICON_NAMES, getIcon } from "@/data/icons";
import { Spinner } from "@/components/States";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, X, Save, Loader2, Eye, EyeOff } from "lucide-react";

const EMPTY = { label: "", slug: "", icon: "Store", color: "#E54D2E", order: 99, is_active: true };

function CategoryForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (initial?.id) await api.put(`/admin/categories/${initial.id}`, form);
      else await api.post("/admin/categories", form);
      toast.success(initial?.id ? "Categoría actualizada" : "Categoría creada");
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo guardar");
      setSaving(false);
    }
  };

  const Preview = getIcon(form.icon);
  return (
    <div className="fixed inset-0 z-[80] flex justify-center overflow-y-auto bg-black/50 p-0 sm:p-4">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="h-fit min-h-screen w-full max-w-lg bg-white sm:min-h-0 sm:rounded-2xl" data-testid="category-form">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-black/5 bg-white px-5 py-4">
          <h2 className="font-display text-xl font-bold">{initial?.id ? "Editar categoría" : "Nueva categoría"}</h2>
          <button onClick={onClose} data-testid="category-form-close" className="rounded-full bg-[#F3F0E6] p-2"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4 p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl" style={{ backgroundColor: `${form.color}18` }}>
              <Preview size={26} style={{ color: form.color }} />
            </div>
            <label className="flex flex-1 flex-col gap-1">
              <span className="text-xs font-bold text-[#5C5C5C]">Nombre *</span>
              <input value={form.label} onChange={(e) => set("label", e.target.value)} required data-testid="category-label" className="rounded-lg border border-black/10 px-3 py-2 text-sm outline-none focus:border-[#E54D2E]" placeholder="Ej. Veterinaria" />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-bold text-[#5C5C5C]">Color</span>
              <input type="color" value={form.color} onChange={(e) => set("color", e.target.value)} data-testid="category-color" className="h-10 w-full rounded-lg border border-black/10" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-bold text-[#5C5C5C]">Orden</span>
              <input type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className="rounded-lg border border-black/10 px-3 py-2 text-sm outline-none" />
            </label>
          </div>

          <div>
            <span className="text-xs font-bold text-[#5C5C5C]">Ícono</span>
            <div className="no-scrollbar mt-1 grid max-h-40 grid-cols-8 gap-2 overflow-y-auto rounded-lg border border-black/10 p-2" data-testid="icon-picker">
              {ICON_NAMES.map((n) => {
                const Ic = getIcon(n);
                const active = form.icon === n;
                return (
                  <button key={n} type="button" onClick={() => set("icon", n)} data-testid={`icon-${n}`} className={`flex h-9 items-center justify-center rounded-lg ${active ? "bg-[#1A1A1A]" : "bg-[#F3F0E6]"}`}>
                    <Ic size={16} className={active ? "text-white" : "text-[#1A1A1A]"} />
                  </button>
                );
              })}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} /> Activa (visible en la app)
          </label>

          {error && <p className="text-sm font-semibold text-[#E54D2E]" data-testid="category-form-error">{error}</p>}
          <button type="submit" disabled={saving} data-testid="category-form-submit" className="flex items-center justify-center gap-2 rounded-xl bg-[#E54D2E] py-3.5 font-bold text-white disabled:opacity-60">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Guardar categoría
          </button>
        </form>
      </motion.div>
    </div>
  );
}

export default function AdminCategories() {
  const { refreshCategories } = useData();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/categories", { params: { include_inactive: true } }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar la categoría "${c.label}"?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Categoría eliminada");
      load(); refreshCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const onSaved = () => { setFormOpen(false); load(); refreshCategories(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold">Categorías</h2>
          <p className="text-xs text-[#5C5C5C]">Crea y administra las categorías del directorio.</p>
        </div>
        <button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-category-button" className="flex items-center gap-1.5 rounded-xl bg-[#E54D2E] px-4 py-2.5 text-sm font-bold text-white">
          <Plus size={18} /> Nueva
        </button>
      </div>

      {loading ? <Spinner /> : (
        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {items.map((c) => {
            const Ic = getIcon(c.icon);
            return (
              <div key={c.id} data-testid={`category-row-${c.slug}`} className="flex items-center gap-3 rounded-xl border border-black/5 bg-white px-4 py-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: `${c.color}18` }}>
                  <Ic size={18} style={{ color: c.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-bold text-[#1A1A1A]">{c.label}</p>
                  <p className="truncate text-xs text-[#5C5C5C]">/{c.slug} · orden {c.order}</p>
                </div>
                {!c.is_active ? <EyeOff size={16} className="text-[#5C5C5C]" /> : <Eye size={16} className="text-[#16A34A]" />}
                <button onClick={() => { setEditing(c); setFormOpen(true); }} data-testid={`edit-category-${c.slug}`} className="rounded-lg p-2 hover:bg-[#F3F0E6]"><Pencil size={16} /></button>
                <button onClick={() => remove(c)} data-testid={`delete-category-${c.slug}`} className="rounded-lg p-2 hover:bg-red-50"><Trash2 size={16} className="text-[#E54D2E]" /></button>
              </div>
            );
          })}
        </div>
      )}

      {formOpen && <CategoryForm initial={editing} onClose={() => setFormOpen(false)} onSaved={onSaved} />}
    </div>
  );
}
